import type {BolSalesChannel} from '@/types/salesChannels/bolSalesChannels';
import type {
  CreateSalesChannelRequest,
  ImportProductsRequest,
  RenameSalesChannelRequest,
  PostDisableOrderLoadingResponse,
  PostEnableOrderLoadingResponse,
  PostRequestDelegationResponse,
  SalesChannel,
} from '@myparcel-frontend/bol-ecommerce-service';
import {useBolEcommerceService} from '@myparcel-frontend/bol-ecommerce-service';
import {useSalesChannelStore} from '@/store/salesChannelStore/salesChannelStore';
import logToMonitor from '@/utils/logging';

export default function useBolEcommerceSalesChannel() {
  const salesChannelStore = useSalesChannelStore();

  const getBolSalesChannels = async (shopIds: string[]): Promise<BolSalesChannel[] | []> => {
    const client = useBolEcommerceService();

    try {
      const response = await client.getSalesChannels(shopIds);
      const result: BolSalesChannel[] = [];

      if (!response || !response?.results?.length) {
        return result;
      }

      for (const salesChannel of response.results) {
        const bolSalesChannel: BolSalesChannel = {
          ...salesChannel,
          ecommercePlatform: 'BOL',
        };

        const newSalesChannel = salesChannelStore.addSalesChannel(bolSalesChannel);

        result.push(newSalesChannel);
      }

      return result;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Fetching Bol sales channels failed in useBol composable',
      });

      throw error;
    }
  };

  const createBolSalesChannel = async (data: CreateSalesChannelRequest) => {
    const client = useBolEcommerceService();

    try {
      const response = await client.createSalesChannel(data);

      const newBolSalesChannel: BolSalesChannel = {
        ...response,
        ecommercePlatform: 'BOL',
      };

      salesChannelStore.addSalesChannel(newBolSalesChannel);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Creating Bol sales channel failed in useBol composable',
        _myparcel_sales_channel_payload: data,
      });

      return undefined;
    }
  };

  const removeBolSalesChannel = async (salesChannelId: string) => {
    const client = useBolEcommerceService();

    try {
      const response = await client.removeSalesChannel(salesChannelId);

      salesChannelStore.removeSalesChannel(salesChannelId);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Removing Bol sales channel failed in useBol composable',
        _myparcel_sales_channel_id: salesChannelId,
      });

      throw error;
    }
  };

  const renameSalesChannel = async (data: RenameSalesChannelRequest): Promise<SalesChannel> => {
    const client = useBolEcommerceService();

    try {
      const response = await client.renameSalesChannel(data);

      const updatedBolSalesChannel: BolSalesChannel = {
        ...response,
        ecommercePlatform: 'BOL',
      };

      salesChannelStore.updateSalesChannel(updatedBolSalesChannel);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Renaming Bol sales channel failed in useBol composable',
        _myparcel_sales_channel_id: data.salesChannelId,
        _myparcel_new_name: data.name,
      });

      throw error;
    }
  };

  const enableOrderLoading = async (salesChannelId: string): Promise<PostEnableOrderLoadingResponse> => {
    const client = useBolEcommerceService();

    try {
      const response = await client.enableOrderLoading(salesChannelId);

      salesChannelStore.enableOrderLoading(salesChannelId);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Enabling order loading for Bol sales channel failed',
        _myparcel_sales_channel_id: salesChannelId,
      });

      throw error;
    }
  };

  const disableOrderLoading = async (salesChannelId: string): Promise<PostDisableOrderLoadingResponse> => {
    const client = useBolEcommerceService();

    try {
      const response = await client.disableOrderLoading(salesChannelId);

      salesChannelStore.disableOrderLoading(salesChannelId);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Disabling order loading for Bol sales channel failed',
        _myparcel_sales_channel_id: salesChannelId,
      });

      throw error;
    }
  };

  const requestDelegation = async (salesChannelId: string): Promise<PostRequestDelegationResponse> => {
    const client = useBolEcommerceService();

    const response = await client.requestDelegation(salesChannelId);

    salesChannelStore.updateSalesChannelToDelegationRequested(response.id, {
      authorizeUrl: response.authorizeUrl,
    });

    return response;
  };

  /* istanbul ignore next */
  const fetchBolSalesChannelById = async (salesChannelId: string): Promise<BolSalesChannel | undefined> => {
    const client = useBolEcommerceService();

    try {
      const response = await client.getSalesChannelById(salesChannelId);

      const bolSalesChannel: BolSalesChannel = {
        ...response,
        ecommercePlatform: 'BOL',
      };

      const newSalesChannel = salesChannelStore.addSalesChannel(bolSalesChannel);

      return newSalesChannel;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Fetching Bol sales channel by id failed',
        _myparcel_sales_channel_id: salesChannelId,
      });

      throw error;
    }
  };

  const importBolProducts = async (saleschannelIds: ImportProductsRequest['salesChannelIds']) => {
    const client = useBolEcommerceService();

    try {
      const response = await client.importProducts(saleschannelIds);

      return response;
    } catch (error) {
      logToMonitor(error as Error, {
        _myparcel_extra_info: 'Importing Bol products failed in useBol composable',
        _myparcel_sales_channel_ids: saleschannelIds,
      });

      throw error;
    }
  };

  return {
    getBolSalesChannels,
    createBolSalesChannel,
    removeBolSalesChannel,
    renameSalesChannel,
    requestDelegation,
    enableOrderLoading,
    disableOrderLoading,
    fetchBolSalesChannelById,
    importBolProducts,
  };
}
